import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CurrencyMaskInput } from '@/components/MaskedInput';
import { formatCents } from '@/lib/format';
import { fromLocalDatetime, rankingPrizePool, toLocalDatetime, type RankingConfig } from '@/lib/campaigns/rankingCompetition';
import { RankingCampaignNotice } from './RankingCampaignNotice';
import { QueueTimezoneSelect } from '@/components/publishQueues/QueueTimezoneSelect';

export function RankingCampaignFields({ config, currency, onChange, campaignId, campaignStatus }: {
  config: RankingConfig;
  currency: 'BRL' | 'USD';
  onChange: (next: RankingConfig) => void;
  campaignId?: string;
  campaignStatus?: string;
}) {
  const { t } = useTranslation();
  return <fieldset className="space-y-5" data-testid="ranking-campaign-fields">
    <legend className="mb-4 font-semibold">{t('ranking.settings')}</legend>
    <RankingCampaignNotice campaignId={campaignId} campaignStatus={campaignStatus} />
    <div className="space-y-2 rounded-lg border p-4">
      <div className="flex items-center justify-between gap-4"><Label htmlFor="ranking-auto-admission">{t('ranking.autoApproval')}</Label><Switch id="ranking-auto-admission" checked={config.auto_approve_clips === true} onCheckedChange={checked => onChange({ ...config, auto_approve_clips: checked })} /></div>
      <p className="text-sm text-muted-foreground">{t('ranking.autoApprovalHelp')}</p>
    </div>
    <div className="grid gap-4 sm:grid-cols-2">
      <div className="space-y-2">
        <Label htmlFor="ranking-payment">{t('ranking.payoutDestination')}</Label>
        <Select value={config.payout_destination} onValueChange={value => onChange({ ...config, payout_destination: value as RankingConfig['payout_destination'] })}>
          <SelectTrigger id="ranking-payment"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="external">{t('ranking.payment.external')}</SelectItem><SelectItem value="wallet">{t('ranking.payment.wallet')}</SelectItem></SelectContent>
        </Select>
        <p className="text-sm text-muted-foreground">{t(`ranking.paymentHelp.${config.payout_destination}`)}</p>
      </div>
      {config.payout_destination === 'external' && <div className="space-y-2">
        <Label htmlFor="ranking-whatsapp">{t('ranking.organizerWhatsapp')}</Label>
        <Input id="ranking-whatsapp" type="tel" placeholder="+5511999999999" value={config.organizer_whatsapp ?? ''} onChange={event => onChange({ ...config, organizer_whatsapp: event.target.value || null })} />
        <p className="text-sm text-muted-foreground">{t('ranking.organizerWhatsappHelp')}</p>
      </div>}
      <div className="space-y-2">
        <Label htmlFor="ranking-minimum">{t('ranking.minimum')}</Label>
        <Input id="ranking-minimum" type="number" min={0} step={1} value={config.min_total_views} onChange={event => onChange({ ...config, min_total_views: Number(event.target.value) })} />
        <p className="text-sm text-muted-foreground">{t('ranking.minimumHelp')}</p>
      </div>
      <div className="space-y-2">
        <Label htmlFor="ranking-deadline">{t('ranking.reportDeadline')}</Label>
        <Input id="ranking-deadline" type="datetime-local" value={toLocalDatetime(config.report_deadline)} onChange={event => onChange({ ...config, report_deadline: fromLocalDatetime(event.target.value) })} />
        <p className="text-sm text-muted-foreground">{t('ranking.localTimeHelp')}</p>
      </div>
      <div className="space-y-2">
        <Label htmlFor="ranking-timezone">{t('ranking.timezone')}</Label>
        <QueueTimezoneSelect id="ranking-timezone" value={config.timezone} onChange={timezone => onChange({ ...config, timezone })} />
      </div>
    </div>
    <div className="space-y-3">
      <h3 className="font-medium">{t('ranking.prizes')}</h3>
      {config.prizes.map((prize, index) => <div key={prize.place} className="flex items-center gap-3">
        <Label htmlFor={`ranking-prize-${index}`} className="min-w-20">{t('ranking.place', { place: prize.place })}</Label>
        <CurrencyMaskInput valueCents={prize.amount_cents} currency={currency} onChangeCents={amount => onChange({ ...config, prizes: config.prizes.map((entry, i) => i === index ? { ...entry, amount_cents: amount ?? 0 } : entry) })} />
        <Button type="button" variant="ghost" size="icon" aria-label={t('ranking.removePrize', { place: prize.place })} onClick={() => onChange({ ...config, prizes: config.prizes.filter((_, i) => i !== index).map((entry, i) => ({ ...entry, place: i + 1 })) })}><Trash2 className="h-4 w-4" /></Button>
      </div>)}
      <Button type="button" variant="outline" disabled={config.prizes.length >= 100} onClick={() => onChange({ ...config, prizes: [...config.prizes, { place: config.prizes.length + 1, amount_cents: 0 }] })}><Plus className="mr-2 h-4 w-4" />{t('ranking.addPrize')}</Button>
      <p className="font-medium">{t('ranking.pool')}: {formatCents(rankingPrizePool(config), currency)}</p>
      <p className="text-sm text-muted-foreground">{t('ranking.unfilledPrizes')}</p>
    </div>
  </fieldset>;
}
